import { ChevronLeft, LayoutGrid, List, Plus, Search } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import tips from "../../../assets/copy/tips";
import ErrorToast from "../../../components/ErrorToast";
import SectionActions from "../../../components/ResumeBuilder/SectionActions";
import SkillsEditorListItems, {
	type GroupedSkill,
	type SkillListItem,
} from "../../../components/ResumeBuilder/SkillsEditorListItems/SkillsEditorListItems";
import SkipSectionDialog from "../../../components/ResumeBuilder/SkipSectionDialog";
import Spinner from "../../../components/Spinner";
import TextEditor from "../../../components/TextEditor";
import Tips from "../../../components/Tips";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import ResumeContext from "../../../context/ResumeContext";
import { FormValidation } from "../../../handlers/formValidation";
import { NavigationHandler } from "../../../handlers/navigationHandler";
import {
	convertArrayToUl,
	convertSplittedSkillsToNormalSkillsArray,
	convertUlToArray,
	getFirstWordSeparedByFullColon,
	type ListItem,
	stripSkillGrouping,
	withWorkHistorySkills,
} from "./../../../handlers/utilityFunctions";
import { resumeObjectToContextFormat } from "../../../handlers/valueTransformation";
import { NEXT_AFTER_SKILLS_TITLE, previousBeforeSkillsTitle, useEducationFirst } from "../../../router/sectionOrder";
import { StaticValue } from "../../../seed/constants";
import { skillsForJobFunction } from "../../../seed/skills/skillsMapping";
import { usePatchResumeSection } from "../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../services/create-resume/resumeSections";
import type { ResumeContextValue } from "../../../types/resume";
import GroupedSkillsItem from "./GroupedSkillsItem";

const ResumeSkills = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const formValidation = new FormValidation();

	const patchSection = usePatchResumeSection();
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const [showSkipDialog, setShowSkipDialog] = useState(false);
	const [showSkillSelection, setShowSkillSelection] = useState(false);

	let { journal } = resumeData;
	let resumeCurrentStatus = journal ? journal.currentStatus : false;

	let isComplete = resumeCurrentStatus == "COMPLETED";

	// Named off the route rather than `navigationFlow.current`, which only moves
	// when a flow button is clicked. Arriving here by `<Link>`, the sidebar or a
	// reload left that cursor at the head of the list, so the button read "Next :
	// Education Information" — the second screen of the flow, not the one after
	// this — and Previous fell through to its default string.
	const educationFirst = useEducationFirst();
	const previousTxt = isComplete ? "Exit" : `Previous : ${previousBeforeSkillsTitle(educationFirst)}`;
	const nextTxt = isComplete ? "Save & Close" : `Next : ${NEXT_AFTER_SKILLS_TITLE}`;

	function previous() {
		navigationHandler.getPrevious(navigationHandler.template.navigationFlow, link, isComplete);

		navigate(-1);
	}

	const [skillList, setSkillList] = useState<SkillListItem[]>([]);
	const [userInput, setUserInput] = useState(resumeData.contactInfo.resumeTitle);

	useEffect(() => {
		setUserInput(resumeData.contactInfo.resumeTitle);
		addSuggestionsToList();
	}, [resumeData]);

	function addSuggestionsToList() {
		const jobFunction = resumeData.contactInfo.resumeFunction.toString().toLowerCase();

		const suggestions = skillsForJobFunction(jobFunction);
		setSkillList([]);

		if (suggestions && suggestions.length > 0) {
			let list = [...suggestions];

			list.map((item, index) => {
				if (item !== "") {
					const value = {
						id: index,
						title: item,
						added: false,
					};
					setSkillList((prev) => {
						return [...prev, value];
					});
				}
				return setSkillList((prev) => {
					//remove empty values
					return [...prev].filter((n) => n.title.length !== 0);
				});
			});
		}
	}

	useEffect(() => {
		addSuggestionsToList();
	}, [userInput]);

	//Declaring the skills state
	const [skills, setSkills] = useState(resumeData.skills);

	const [filteredSkillList, setFilteredSkillList] = useState<SkillListItem[]>([]);

	useEffect(() => {
		setFilteredSkillList(skillList);
	}, [skillList]);

	const [errors] = useState([]);
	const [isInputCompleted, setIsInputCompleted] = useState(false);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const [serverErrors, setServerErrors] = useState([]);

	let resumeId = cookies[staticValues.localStorageResumeId];

	// `progressStatus` is a server virtual, so the rail only learns a section is
	// finished from a ledger response. Dropping the one this save returns left
	// Skills looking untouched until the next full load, even though the save had
	// gone through — the same hydration the education and work-history saves do.
	function onSuccess(ledger: unknown) {
		resumeObjectToContextFormat(ledger, setResumeData);
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/brief-summary");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as { response: { data: { message: string | string[] } } }).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message];
				});
	}

	//submission handler
	const addSkills = () => {
		const formattedSkillsData = convertSplittedSkillsToNormalSkillsArray(groupedSkills);
		const customSkillsData = convertUlToArray(customSkills);
		const skillsData = formattedSkillsData.concat(customSkillsData);
		const formattedUlSkillsData = convertArrayToUl(formattedSkillsData);
		const ulSkillsData = formattedUlSkillsData.concat(customSkills);

		if (isInputCompleted) {
			setResumeData((prevState) => {
				return {
					...prevState,
					skills: ulSkillsData,
				};
			});

			patchSection.mutate(
				{
					resumeId,
					section: RESUME_SECTIONS.SKILLS,
					data: { data: skillsData.filter((skill) => skill.value !== "") },
				} as never,
				{ onSuccess, onError },
			);
		}
	};

	useEffect(() => {
		const validation = formValidation.validateSkillsDetails(errors, skills);
		if (!validation.status) {
			setIsInputCompleted(false);
		} else {
			setIsInputCompleted(true);
		}
	}, [skills]);

	// What a reader picked while writing up each job starts this screen off, so
	// the same competency is not asked for twice.
	const startingSkills = useMemo(
		() => withWorkHistorySkills(resumeData.skills, resumeData.workHistory),
		[resumeData.skills, resumeData.workHistory],
	);

	useEffect(() => {
		setSkills(startingSkills);
	}, [startingSkills]);

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/brief-summary");
	};

	const [addCustomCategory, setAddCustomCategory] = useState(false);
	const [groupedSkills, setGroupedSkills] = useState<GroupedSkill[]>([]);
	const [customSkills, setCustomSkills] = useState("");
	const [showGroupedView, setShowGroupedView] = useState(false);

	function getGroupedAndCustomSkills(data) {
		let newCustomSkills: ListItem[] = [];
		let skillsInGroup = convertUlToArray(data)
			.map((skillObject) => {
				const splitArray = skillObject.value.split(/:(.+)/);
				let wordBeforeColon = splitArray[0].trim();
				if (splitArray.length > 1) {
					let groupedSkills = [];
					groupedSkills = groupedSkills.concat(
						splitArray[1]
							.trim()
							.split(",")
							.map((skillItem) => {
								// Trim here, not just at the ends: splitting on "," leaves a leading
								// space on every item after the first, and the rebuild joins with
								// ", " again, so each save doubled the gap in the stored value.
								// Strip the grouping braces too, or they end up on the resume.
								return { added: true, skill: stripSkillGrouping(skillItem) };
							}),
					);
					return { title: wordBeforeColon, skills: groupedSkills, collapsed: true };
				}
				newCustomSkills.push({ bp: true, value: wordBeforeColon });
			})
			.filter((item) => item !== undefined);
		setGroupedSkills(skillsInGroup);
		setCustomSkills(convertArrayToUl(newCustomSkills));
		newCustomSkills.length > 0 && setAddCustomCategory(true);
	}

	const collapseGroupedSkills = (groupedSkillIndex) => {
		let newGroupedSkills = [
			...groupedSkills.map((skillGroup) => ({
				...skillGroup,
				collapsed: skillGroup === groupedSkills[groupedSkillIndex] ? !skillGroup.collapsed : skillGroup.collapsed,
			})),
		];
		setGroupedSkills(newGroupedSkills);
	};

	const removeGroupedSkills = (skillTitle) => {
		let newGroupedSkills = groupedSkills.filter((skillItem) => skillItem.title !== skillTitle);
		setGroupedSkills(newGroupedSkills);

		const formattedSkillsData = convertSplittedSkillsToNormalSkillsArray(newGroupedSkills);
		const formattedUlSkillsData = convertArrayToUl(formattedSkillsData);
		const ulSkillsData = formattedUlSkillsData.concat(customSkills);
		setSkills(ulSkillsData);

		let newFilteredSkillList = filteredSkillList;
		const indexChanged = newFilteredSkillList.findIndex(
			(changed) => getFirstWordSeparedByFullColon(changed.title) === skillTitle,
		);
		if (indexChanged !== -1) {
			newFilteredSkillList[indexChanged].added = false;
			setFilteredSkillList(newFilteredSkillList);
		}
	};

	const onRemoveSingleSkill = (groupedSkillIndex, singleSkillIndex) => {
		let newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
		}));

		newGroupedSkills[groupedSkillIndex].skills = newGroupedSkills[groupedSkillIndex].skills.filter(
			(_, index) => index !== singleSkillIndex,
		);

		setGroupedSkills(newGroupedSkills);

		const formattedSkillsData = convertSplittedSkillsToNormalSkillsArray(newGroupedSkills);
		const formattedUlSkillsData = convertArrayToUl(formattedSkillsData);
		const ulSkillsData = formattedUlSkillsData.concat(customSkills);
		setSkills(ulSkillsData);
	};

	const onAddCustomSkill = (groupedSkillIndex) => {
		let newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
		}));

		newGroupedSkills[groupedSkillIndex].skills = [
			...newGroupedSkills[groupedSkillIndex].skills,
			{ added: true, skill: "" },
		];

		setGroupedSkills(newGroupedSkills);
	};

	const onSaveCustomSkillAdded = (groupedSkillIndex, singleSkillIndex, skillAdded) => {
		let newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
		}));

		newGroupedSkills[groupedSkillIndex].skills[singleSkillIndex].skill = skillAdded;

		setGroupedSkills(newGroupedSkills);

		const formattedSkillsData = convertSplittedSkillsToNormalSkillsArray(newGroupedSkills);
		const formattedUlSkillsData = convertArrayToUl(formattedSkillsData);
		const ulSkillsData = formattedUlSkillsData.concat(customSkills);
		setSkills(ulSkillsData);
	};

	const toggleEditorVisibility = () => {
		let newGroupedSkills = groupedSkills.map((group) => ({
			...group,
			skills: group.skills.map((skill) => ({ ...skill })),
			collapsed: !addCustomCategory,
		}));
		setGroupedSkills(newGroupedSkills);
		setAddCustomCategory(!addCustomCategory);
	};

	useEffect(() => {
		getGroupedAndCustomSkills(startingSkills);
	}, [startingSkills]);

	const syncToGroupedView = (newSkills) => {
		getGroupedAndCustomSkills(newSkills);
	};

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<Dialog open={showSkillSelection} onOpenChange={setShowSkillSelection}>
				<DialogContent className="sm:max-w-3xl">
					<DialogHeader>
						<DialogTitle>Select Skills</DialogTitle>
					</DialogHeader>
					<div className="flex items-stretch">
						<Input
							type="text"
							placeholder={userInput}
							onChange={(e) => {
								const input = e.target.value;
								if (input.trim() !== "") {
									setFilteredSkillList((prev) =>
										prev.filter((skill) => skill.title.toLowerCase().includes(input.toLowerCase())),
									);
								} else {
									setFilteredSkillList(skillList);
								}
							}}
							className="rounded-r-none"
						/>
						<Button type="button" className="rounded-l-none md:px-10" aria-label="Search">
							<Search className="size-4" />
						</Button>
					</div>
					<div className="rounded-md border border-[#d5dbe1] border-dashed bg-[#f8f8f8]">
						<SkillsEditorListItems
							editorListItems={filteredSkillList}
							editorContent={skills}
							setEditorContent={setSkills}
							setEditorListItems={setFilteredSkillList}
							groupedSkills={groupedSkills}
							setGroupedSkills={setGroupedSkills}
						/>
					</div>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowSkillSelection(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<div className="min-h-screen">
				{serverErrors.map((item, index) => {
					return <ErrorToast error={item} key={index} />;
				})}
				<div className="mb-3 flex items-start justify-between gap-3">
					<div className="text-black/60 text-sm leading-5">
						{/* The server only counts this section complete at three or more entries
						    (`detailSectionCompletion(skills, 3)`). Nothing said so, so a reader
						    who added two moved on and later found Skills marked INCOMPLETE. */}
						Highlight your key competencies and technical proficiencies. Add at least three.
					</div>
					<Tips content={tips.skills} />
				</div>
				<div className="mb-3 flex items-center gap-4">
					<Button type="button" variant="secondary" size="lg" onClick={() => setShowSkillSelection(true)}>
						<Plus className="mr-2 size-4" />
						Pick Skills to Add
					</Button>
					<div className="inline-flex overflow-hidden rounded-md border border-gray-500" role="group">
						<button
							type="button"
							aria-label="Grouped view"
							className={`flex items-center justify-center px-2.5 py-1.5 ${showGroupedView ? "bg-gray-500 text-white" : "bg-white text-gray-500 hover:bg-gray-100"}`}
							onClick={() => setShowGroupedView(true)}
						>
							<LayoutGrid className="size-4" />
						</button>
						<button
							type="button"
							aria-label="List view"
							className={`flex items-center justify-center px-2.5 py-1.5 ${!showGroupedView ? "bg-gray-500 text-white" : "bg-white text-gray-500 hover:bg-gray-100"}`}
							onClick={() => setShowGroupedView(false)}
						>
							<List className="size-4" />
						</button>
					</div>
				</div>
				<div>
					{showGroupedView ? (
						<>
							{groupedSkills.map((item, groupedSkillIndex) => {
								if (item.title === "") {
									return null;
								}

								return (
									<GroupedSkillsItem
										key={groupedSkillIndex}
										item={item}
										collapseGroupedSkills={collapseGroupedSkills}
										groupedSkillIndex={groupedSkillIndex}
										removeGroupedSkills={removeGroupedSkills}
										onSaveCustomSkillAdded={onSaveCustomSkillAdded}
										onRemoveSingleSkill={onRemoveSingleSkill}
										onAddCustomSkill={onAddCustomSkill}
									/>
								);
							})}

							{addCustomCategory && (
								<div className="editor-wrapper">
									<TextEditor
										value={customSkills}
										setValue={(value) => {
											setCustomSkills(value);
											const formattedSkillsData = convertSplittedSkillsToNormalSkillsArray(groupedSkills);
											const formattedUlSkillsData = convertArrayToUl(formattedSkillsData);
											const ulSkillsData = formattedUlSkillsData.concat(value);
											setSkills(ulSkillsData);
										}}
										placeholder="Add custom skills..."
										toggleEditorVisibility={toggleEditorVisibility}
									/>
								</div>
							)}
							{!addCustomCategory && (
								<div className="flex cursor-pointer items-center" onClick={toggleEditorVisibility}>
									<Plus className="mr-2 size-4" />
									<span className="font-semibold text-[#7a7a7a] text-sm leading-5">Add custom category</span>
								</div>
							)}
						</>
					) : (
						<div className="editor-wrapper">
							<TextEditor
								value={skills}
								setValue={(value) => {
									setSkills(value);
									syncToGroupedView(value);
								}}
								placeholder="Enter your skills..."
							/>
						</div>
					)}
					<SectionActions className="flex items-center justify-between gap-3">
						{/* Phone screens have no room for two destination names, so back keeps
						    only its arrow there and Next takes the width it frees. */}
						<Button type="button" variant="ghost" className="shrink-0" onClick={() => previous()}>
							<ChevronLeft className="size-4 md:hidden" />
							<span className="hidden md:inline">{previousTxt}</span>
							<span className="sr-only md:hidden">{previousTxt}</span>
						</Button>
						<Button
							type="button"
							className="flex-1 md:flex-none"
							onClick={() => {
								if (!isInputCompleted) {
									setShowSkipDialog(true);
								}
								addSkills();
							}}
						>
							{patchSection.isPending ? (
								<>
									Loading <Spinner />
								</>
							) : (
								nextTxt
							)}
						</Button>
					</SectionActions>
				</div>
			</div>
		</>
	);
};

export default ResumeSkills;
